import type { IconName } from 'lucide-react/dynamic'
import type { FilterGroup } from './deviceFilters'

export type DeviceStatusIcon = IconName

/** Hex color (`#3b82f6`). */
export type DeviceStatusColor = string

export interface DeviceStatusDefinition {
	name: string
	/** Reuses the Device Management group model, so it converts straight to a /search payload. Empty on the default state. */
	condition: FilterGroup[]
	icon: DeviceStatusIcon
	color: DeviceStatusColor
	/**
	 * The fallback state: it matches on nothing of its own and claims every device no other state
	 * matched. At most one per column, and it takes no part in match order — it is only ever reached
	 * once every other state has failed, whatever position it holds in the array.
	 */
	isDefault?: boolean
}

/**
 * The `expressions` of a derived device state: the states a device can resolve to.
 *
 * Array position is match order — a device matching several states takes the first, except for the
 * `isDefault` state which is always considered last. The backend stores this as opaque JSONB, so the
 * structured `condition` round-trips into the editor as-is.
 */
export type DeviceStatusExpression = DeviceStatusDefinition[]

export const isDeviceStatusExpression = (value: unknown): value is DeviceStatusExpression => Array.isArray(value)

/** The fallback state, or null on a column that defines none. */
export const defaultStatusDefinition = (expression: DeviceStatusExpression): DeviceStatusDefinition | null =>
	expression.find(definition => definition.isDefault === true) ?? null

/** The states that match on conditions, in match order. */
export const matchableStatusDefinitions = (expression: DeviceStatusExpression): DeviceStatusDefinition[] =>
	expression.filter(definition => definition.isDefault !== true)
